import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing.jsx";
import Features from "@/pages/Features.jsx";
import HowItWorks from "@/pages/HowItWorks.jsx";
import UseCases from "@/pages/UseCases.jsx";
import Pricing from "@/pages/Pricing.jsx";
import FAQ from "@/pages/FAQ.jsx";
import Contact from "@/pages/Contact.jsx";
import Login from "@/pages/Login.jsx";
import Signup from "@/pages/Signup.jsx";

import AppShell from "@/components/app/AppShell.jsx";
import Dashboard from "@/pages/Dashboard.jsx";
import HistoryPage from "@/pages/History.jsx";
import InsightsPage from "@/pages/Insights.jsx";
import NotesTakerPage from "@/pages/NotesTaker.jsx";
import DictionaryPage from "@/pages/Dictionary.jsx";
import SnippetsPage from "@/pages/Snippets.jsx";
import ScratchpadPage from "@/pages/Draftpad.jsx";
import BillingPage from "@/pages/Billing.jsx";
import SettingsPage from "@/pages/Settings.jsx";
import ShortcutsPage from "@/pages/Shortcuts.jsx";
import PaymentVerify from "@/pages/PaymentVerify.jsx";

import AdminShell from "@/components/admin/AdminShell.jsx";
import AdminDashboard from "@/pages/admin/AdminDashboard.jsx";
import AdminUsers from "@/pages/admin/AdminUsers.jsx";
import AdminPayments from "@/pages/admin/AdminPayments.jsx";
import AdminManualPayments from "@/pages/admin/AdminManualPayments.jsx";
import AdminPlans from "@/pages/admin/AdminPlans.jsx";
import AdminCoupons from "@/pages/admin/AdminCoupons.jsx";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements.jsx";
import AdminSettings from "@/pages/admin/AdminSettings.jsx";
import AdminSupport from "@/pages/admin/AdminSupport.jsx";
import AdminBroadcast from "@/pages/admin/AdminBroadcast.jsx";

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-brand-muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/use-cases" element={<UseCases />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/payment/verify" element={<Protected><PaymentVerify /></Protected>} />

          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<Dashboard />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="insights" element={<InsightsPage />} />
            <Route path="notes" element={<NotesTakerPage />} />
            <Route path="dictionary" element={<DictionaryPage />} />
            <Route path="snippets" element={<SnippetsPage />} />
            <Route path="scratchpad" element={<ScratchpadPage />} />
            <Route path="draftpad" element={<ScratchpadPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="shortcuts" element={<ShortcutsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route path="/admin" element={<Protected admin><AdminShell /></Protected>}>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="manual-payments" element={<AdminManualPayments />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="broadcast" element={<AdminBroadcast />} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
        </Routes>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
